// Formatting shared by the Replay v2 pages.

/** What the models people compare are called, by the id without its host or hints. */
const NAMES: Record<string, string> = {
  'deepseek-v4.1-flash': 'DeepSeek v4.1 Flash', 'glm-5.3': 'GLM 5.3', 'qwen3.8-max': 'Qwen 3.8 Max',
  'muse-wonder-1.0': 'Muse Wonder 1.0', 'muse-spark-1.3': 'Muse Spark 1.3', 'gemini-4-argon': 'Gemini 4 Argon',
  'muse-spark-open': 'Muse Spark Open', 'deepseek-v4.1-flash-1017': 'DeepSeek v4.1 Flash 1017',
}
/** Who a model's calls go through, by the id's first part: the Tuneloop gateway for "tuneloop/…", else the provider. */
const ROUTES: Record<string, string> = { tuneloop: 'Tuneloop', bedrock: 'Bedrock', 'amazon-bedrock': 'Bedrock', openai: 'OpenAI', openrouter: 'OpenRouter' }

/** A model id as people say it, with who its calls go through (the same model can run both ways):
 *  "us.anthropic.claude-opus-5-5" → "Opus 5.5 (Bedrock)", "tuneloop/bedrock/claude-opus-5-5[1m]" → "Opus 5.5 (Tuneloop)",
 *  "tuneloop/fireworks/deepseek-v4.1-flash[1m]" → "DeepSeek v4.1 Flash (Tuneloop)". */
export function modelName(id: string): string {
  if (!id) return ''
  const host = id.includes('/') ? id.split('/')[0]! : /^(gpt|codex)/.test(id) ? 'openai' : 'bedrock'
  return bareName(id) + ' (' + (ROUTES[host] ?? host) + ')'
}

export function bareName(id: string): string {
  const last = (id.split('/').pop() || id).replace(/\[1m\]$/, '')
  if (NAMES[last]) return NAMES[last]
  const bare = last
    .replace(/^bedrock-/, '')
    .replace(/^us\.anthropic\./, '')
    .replace(/^claude-/, '')
    .replace(/-\d{8}(-v\d+:\d+)?$/, '')
  const gpt = bare.match(/^gpt-([\d.]+)-([a-z]+)$/)
  if (gpt) return 'GPT ' + gpt[1] + ' ' + gpt[2]!.charAt(0).toUpperCase() + gpt[2]!.slice(1)
  const m = bare.match(/^([a-z]+)-(\d+)(?:-(\d+))?$/)
  return m ? m[1]!.charAt(0).toUpperCase() + m[1]!.slice(1) + ' ' + m[2] + (m[3] ? '.' + m[3] : '') : bare
}

export const pct = (x: number | null | undefined, digits = 0): string =>
  x == null || !isFinite(x) ? '—' : (x * 100).toFixed(digits) + '%'

export const money = (x: number | null | undefined): string =>
  x == null || !isFinite(x) ? '—' : x > 0 && x < 0.01 ? '<$0.01' : '$' + x.toFixed(2)

/** A mean own score (0–1) as points out of 100. */
export const points = (x: number | null | undefined): string => (x == null || !isFinite(x) ? '—' : Math.round(x * 100).toString())

export const signed = (x: number): string => (x > 0 ? '+' : x < 0 ? '−' : '') + Math.abs(Math.round(x))

export function duration(s: number | null | undefined): string {
  if (s == null || !isFinite(s)) return '—'
  if (s < 90) return Math.round(s) + 's'
  if (s < 3600) return (s / 60).toFixed(1) + 'm'
  return (s / 3600).toFixed(1) + 'h'
}

export function day(iso: string): string {
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Task types, in display order, with a short name. */
export const TYPES: [string, string][] = [
  ['coding:explore', 'Explore'], ['coding:plan', 'Plan'], ['coding:implement', 'Implement'], ['coding:debug', 'Debug'],
  ['coding:review', 'Review'], ['coding:testing', 'Testing'], ['writing:doc', 'Doc'], ['writing:email', 'Email'],
  ['writing:summary', 'Summary'], ['strategy', 'Strategy'], ['model_training', 'Model training'], ['other', 'Other'],
]
export const COMPLEXITY = ['trivial', 'routine', 'substantial', 'open-ended']
/** Task-type colours for composition bars: Task Replay's, plus the types it does not list. */
export const TYPE_COLORS: Record<string, string> = {
  'coding:explore': '#0f7a55', 'coding:plan': '#3b6ea5', 'coding:implement': '#bf6a2e', 'coding:debug': '#9a4a3a',
  'coding:review': '#8a5fb0', 'coding:testing': '#3f9a8c', 'writing:email': '#c99a3f', 'writing:doc': '#a24f63',
  'writing:summary': '#6f7b3d', strategy: '#5a6b8a', model_training: '#7a6f5a', other: '#8a857a',
}
export const typeName = (key: string): string => TYPES.find((t) => t[0] === key)?.[1] ?? key
